import { CaseData } from '../../types/case';
import { debugError } from '../../utils/debug';
import type { RecalculateOptions } from '@/calcs/calcRuntimeContext';

/** Lazy-loaded calcs: loaded on first use (e.g. when opening/editing a case) to keep main bundle smaller. */
const CALCS_LOAD_TIMEOUT_MS = 10000;
const CALCS_LOAD_ERROR_MESSAGE = 'Unable to load calculator. Please refresh the page.';

let calcsPromise: Promise<{ recalculate: (data: CaseData, options?: RecalculateOptions) => CaseData; validateInputVersioned: (data: CaseData) => CaseData }> | null = null;
let onCalcsLoadFailure: ((message: string) => void) | null = null;

export function registerCalcsLoadFailure(callback: ((message: string) => void) | null): void {
    onCalcsLoadFailure = callback;
}

export function getCalcs(): Promise<{ recalculate: (data: CaseData, options?: RecalculateOptions) => CaseData; validateInputVersioned: (data: CaseData) => CaseData }> {
    if (!calcsPromise) {
        let timeoutId: ReturnType<typeof setTimeout>;
        const loadPromise = Promise.all([
            import('@/calcs/index'),
            import('@/calcs/versions/versionRouter')
        ])
            .then(([c, v]) => {
                clearTimeout(timeoutId);
                return { recalculate: c.recalculate, validateInputVersioned: v.validateInputVersioned };
            })
            .catch((err) => {
                const message = CALCS_LOAD_ERROR_MESSAGE;
                debugError('SimpleCaseContext: Calcs chunk failed to load', err);
                onCalcsLoadFailure?.(message);
                throw new Error(message);
            });
        const timeoutPromise = new Promise<never>((_, reject) => {
            timeoutId = setTimeout(() => {
                onCalcsLoadFailure?.(CALCS_LOAD_ERROR_MESSAGE);
                reject(new Error(CALCS_LOAD_ERROR_MESSAGE));
            }, CALCS_LOAD_TIMEOUT_MS);
        });
        calcsPromise = Promise.race([loadPromise, timeoutPromise]);
    }
    return calcsPromise;
}
